import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '~/store/store';
import { errorMessages } from '~/constants/errorMessages.ts';
import type { TApiApplicationDocument } from '~/modules/covenants/types.ts';
import { saveSignsFromIIT, startIITSigning } from '~/store/iit/thunks.ts';
import type { TIITSign } from '~/store/iit/types.ts';
import { DiiaFinalStatuses, DiiaStatuses } from '~/store/diia/types.ts';
import {
  covenantsDiiaSignGetFinalStatus,
  covenantsDiiaSignGetStatus,
  covenantsDiiaSignInitialize,
} from '~/store/diia/thunks/signatureCovenantsThunks.ts';
import { QR_CODE_TIME } from '~/constants/common.ts';
import { useUserAuthToken } from '~/modules/covenants/hooks/useUserAuthToken.ts';

const STATUS_POLL_INTERVAL_MS = 2000;
const STATUS_POLL_ATTEMPTS = Math.ceil((QR_CODE_TIME * 1000) / STATUS_POLL_INTERVAL_MS);

const DIIA_FAILURE_STATUSES = [
  DiiaStatuses.refuse,
  DiiaStatuses.rejected,
  DiiaStatuses.canceled,
  DiiaStatuses.notUsed,
] as const;
const DIIA_FAILURE_STATUSES_SET = new Set<DiiaStatuses>(DIIA_FAILURE_STATUSES);

const isDiiaFailureStatus = (status: DiiaStatuses | null): boolean =>
  !!status && DIIA_FAILURE_STATUSES_SET.has(status);

const canFinalizeDiiaStatus = (status: DiiaStatuses | null): boolean =>
  status === DiiaStatuses.done || status === DiiaStatuses.processing;

export const useCovenantsSigning = ({
  contragentId,
  applicationId,
  documents,
  onSuccess,
  onError,
  setDeepLink,
  onDiiaInitialize,
  onDiiaSigningStarted,
  onReset,
}: {
  contragentId: number;
  applicationId: string;
  documents: TApiApplicationDocument[];
  onSuccess: () => Promise<void> | void;
  onError: (msg: string) => void;
  setDeepLink?: (link: string) => void;
  onDiiaInitialize?: () => void;
  onDiiaSigningStarted?: () => void;
  onReset?: () => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const token = useUserAuthToken();
  const diiaAttemptRef = useRef(0);
  const diiaSigningStartedRef = useRef(false);
  const activeStatusRequestRef = useRef<{ abort: () => void } | null>(null);
  const signableDocuments = documents.filter(
    (doc): doc is TApiApplicationDocument & { id: string } => typeof doc.id === 'string'
  );
  const signableDocumentsKey = signableDocuments.map((doc) => `${doc.id}:${doc.version}`).join('|');

  const getErrorMessage = (error: unknown) =>
    (error as { message?: string; data?: { message?: string } } | undefined)?.message ||
    (error as { data?: { message?: string } } | undefined)?.data?.message ||
    errorMessages.SERVICE_UNAVAILABLE;

  const isAttemptActive = (attemptId: number) => diiaAttemptRef.current === attemptId;

  const cancelActiveDiiaAttempt = () => {
    diiaAttemptRef.current += 1;
    activeStatusRequestRef.current?.abort();
    activeStatusRequestRef.current = null;
  };

  const resetSigningSession = () => {
    cancelActiveDiiaAttempt();
    diiaSigningStartedRef.current = false;
    setDeepLink?.('');
    onReset?.();
  };

  const beginDiiaAttempt = () => {
    cancelActiveDiiaAttempt();
    diiaSigningStartedRef.current = false;
    setDeepLink?.('');
    return diiaAttemptRef.current;
  };

  const handleDiiaFailure = (message: string, attemptId?: number) => {
    if (attemptId != null && !isAttemptActive(attemptId)) {
      return;
    }

    resetSigningSession();
    onError(message);
  };

  const saveSignedDocumentsAndSubmit = async () => {
    await onSuccess();
  };

  const ensureToken = () => {
    if (token) {
      return token;
    }

    onError(errorMessages.SERVICE_UNAVAILABLE);
    return null;
  };

  const continueDiiaSigning = async (requestId: string, attemptId: number) => {
    const statusToFinalize = await waitForStatusDone(requestId, attemptId);

    if (!isAttemptActive(attemptId)) {
      return;
    }

    if (
      !statusToFinalize ||
      isDiiaFailureStatus(statusToFinalize) ||
      !canFinalizeDiiaStatus(statusToFinalize)
    ) {
      if (statusToFinalize === DiiaStatuses.refuse) {
        handleDiiaFailure(errorMessages.DOCUMENT_SIGNING_REFUSED, attemptId);
        return;
      }

      handleDiiaFailure(errorMessages.DOCUMENT_SIGNING_FAILED, attemptId);
      return;
    }

    const finalStatusResponse = await dispatch(
      covenantsDiiaSignGetFinalStatus({
        contragentId,
        applicationId,
        requestId,
      })
    ).unwrap();

    if (!isAttemptActive(attemptId)) {
      return;
    }

    const finalStatus = finalStatusResponse.status as string;
    const isFinalSuccess =
      finalStatus === DiiaFinalStatuses.success || finalStatus === DiiaStatuses.done;

    if (!isFinalSuccess) {
      handleDiiaFailure(errorMessages.DOCUMENT_SIGNING_FAILED, attemptId);
      return;
    }

    resetSigningSession();
    await saveSignedDocumentsAndSubmit();
  };

  const waitForStatusDone = async (requestId: string, attemptId: number) => {
    let lastStatus: DiiaStatuses | null = null;

    for (let attempt = 0; attempt < STATUS_POLL_ATTEMPTS; attempt += 1) {
      if (!isAttemptActive(attemptId)) {
        return null;
      }

      const statusRequest = dispatch(
        covenantsDiiaSignGetStatus({
          contragentId,
          applicationId,
          requestId,
        })
      );
      activeStatusRequestRef.current = statusRequest;

      let statusResponse;

      try {
        statusResponse = await statusRequest.unwrap();
      } catch (error) {
        if (!isAttemptActive(attemptId)) {
          return null;
        }

        throw error;
      } finally {
        if (activeStatusRequestRef.current === statusRequest) {
          activeStatusRequestRef.current = null;
        }
      }

      if (!isAttemptActive(attemptId)) {
        return null;
      }

      const status = statusResponse.status as DiiaStatuses;
      lastStatus = status;

      if (!diiaSigningStartedRef.current && canFinalizeDiiaStatus(status)) {
        diiaSigningStartedRef.current = true;
        onDiiaSigningStarted?.();
      }

      if (status === DiiaStatuses.done) {
        return status;
      }

      if (isDiiaFailureStatus(status)) {
        return status;
      }

      if (attempt < STATUS_POLL_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, STATUS_POLL_INTERVAL_MS));
      }
    }

    return lastStatus;
  };

  const handleIITSignature = async () => {
    if (!signableDocuments.length) {
      onError(errorMessages.NO_FILES_TO_SIGN);
      return;
    }

    const authToken = ensureToken();

    if (!authToken) {
      return;
    }

    const path = `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents/get-hashes-to-sign`;

    try {
      await dispatch(
        startIITSigning({
          path,
          token: authToken,
        })
      ).unwrap();
    } catch (error) {
      onError(getErrorMessage(error));
    }
  };

  const onIITSign = async (signFiles: TIITSign[]) => {
    const authToken = ensureToken();

    if (!authToken) {
      return;
    }

    try {
      const path = `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/documents/save-signed-documents`;

      await dispatch(
        saveSignsFromIIT({
          path,
          token: authToken,
          fileList: signFiles,
        })
      ).unwrap();

      await onSuccess();
    } catch {
      onError(errorMessages.DOCUMENT_SIGNING_FAILED);
    }
  };

  const handleDiiaSignature = async (): Promise<boolean> => {
    const attemptId = beginDiiaAttempt();

    try {
      if (!signableDocuments.length) {
        handleDiiaFailure(errorMessages.NO_FILES_TO_SIGN, attemptId);
        return false;
      }

      if (!ensureToken()) {
        resetSigningSession();
        return false;
      }

      const firstDocId = signableDocuments[0]?.id as string;
      const { requestId, deeplink } = await dispatch(
        covenantsDiiaSignInitialize({
          contragentId,
          applicationId,
          documentId: firstDocId,
        })
      ).unwrap();

      if (!isAttemptActive(attemptId)) {
        return false;
      }

      setDeepLink?.(deeplink || '');
      onDiiaInitialize?.();
      void continueDiiaSigning(requestId, attemptId).catch((error) => {
        if (!isAttemptActive(attemptId)) {
          return;
        }

        handleDiiaFailure(getErrorMessage(error), attemptId);
      });

      return true;
    } catch (error) {
      handleDiiaFailure(getErrorMessage(error), attemptId);
      return false;
    }
  };

  useEffect(() => {
    return () => {
      diiaAttemptRef.current += 1;
      activeStatusRequestRef.current?.abort();
      activeStatusRequestRef.current = null;
    };
  }, [applicationId, contragentId, signableDocumentsKey]);

  return {
    handleIITSignature,
    onIITSign,
    handleDiiaSignature,
    resetSigningSession,
  };
};
